import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../logo.png';
import { AlertTriangle, Plus, X, Search, CheckCircle, Clock, Eye, Zap, Lock } from 'lucide-react';

const TIPOS_CONFLICTO = [
  { value: 'doble_captura',      label: 'Doble captura' },
  { value: 'cambio_de_bando',    label: 'Cambio de bando' },
  { value: 'datos_falsos',       label: 'Datos falsos/sospechosos' },
  { value: 'duplicidad_seccion', label: 'Duplicidad de sección' },
  { value: 'disputa_estructura', label: 'Disputa entre estructuras' },
  { value: 'otro',               label: 'Otro' },
];

const PRIORIDADES = [
  { value: 'alta',  label: '🔴 Alta',  color: 'bg-red-100 text-red-700' },
  { value: 'media', label: '🟡 Media', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'baja',  label: '🟢 Baja',  color: 'bg-green-100 text-green-700' },
];

const ESTATUS = [
  { value: 'pendiente',   label: 'Pendiente',   color: 'bg-yellow-100 text-yellow-700' },
  { value: 'en_revision', label: 'En revisión', color: 'bg-blue-100 text-blue-700' },
  { value: 'resuelto',    label: 'Resuelto',    color: 'bg-green-100 text-green-700' },
];

const ROLES_ACCESO = ['superadmin','coordinador_general','coordinador_territorial','coordinador_sectorial','auditor'];

export default function ConflictosPoliticos() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const puedeAcceder = ROLES_ACCESO.includes(user?.rol);
  const puedeCrear   = ['superadmin','coordinador_general','auditor'].includes(user?.rol);
  const puedeDetectar = ['superadmin','coordinador_general'].includes(user?.rol);

  const [conflictos, setConflictos] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [detectando, setDetectando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  // Filtros
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [filtroPrioridad, setFiltroPrioridad] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');

  // Modal nuevo conflicto
  const [mostrarModal, setMostrarModal] = useState(false);
  const [busquedaPersona, setBusquedaPersona] = useState('');
  const [personasBuscadas, setPersonasBuscadas] = useState([]);
  const [personaSeleccionada, setPersonaSeleccionada] = useState(null);
  const [formConflicto, setFormConflicto] = useState({
    tipo_conflicto: '', descripcion: '', prioridad: 'media'
  });
  const [guardando, setGuardando] = useState(false);

  // Modal resolución
  const [mostrarModalResolver, setMostrarModalResolver] = useState(false);
  const [conflictoResolviendo, setConflictoResolviendo] = useState(null);
  const [formResolver, setFormResolver] = useState({
    estatus: '', resolucion_final: '', prioridad: ''
  });

  useEffect(() => {
    if (puedeAcceder) fetchConflictos();
    else setLoading(false);
  }, [puedeAcceder, filtroEstatus, filtroPrioridad, filtroTipo]);

  const fetchConflictos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtroEstatus)   params.append('estatus', filtroEstatus);
      if (filtroPrioridad) params.append('prioridad', filtroPrioridad);
      if (filtroTipo)      params.append('tipo_conflicto', filtroTipo);
      const res = await api.get(`/conflictos?${params}`);
      setConflictos(res.data.conflictos || []);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscarPersona = async (q) => {
    setBusquedaPersona(q);
    if (q.length < 3) { setPersonasBuscadas([]); return; }
    try {
      const res = await api.get(`/conflictos/buscar?q=${q}`);
      setPersonasBuscadas(res.data.personas || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCrearConflicto = async (e) => {
    e.preventDefault();
    if (!personaSeleccionada) { setMensaje({ tipo: 'error', texto: 'Selecciona una persona.' }); return; }
    setGuardando(true);
    try {
      await api.post('/conflictos', {
        persona_id: personaSeleccionada.id,
        ...formConflicto
      });
      setMensaje({ tipo: 'exito', texto: '✅ Conflicto registrado correctamente.' });
      setMostrarModal(false);
      setPersonaSeleccionada(null);
      setBusquedaPersona('');
      setFormConflicto({ tipo_conflicto: '', descripcion: '', prioridad: 'media' });
      fetchConflictos();
      setTimeout(() => setMensaje(null), 3000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error al crear.' });
    } finally {
      setGuardando(false);
    }
  };

  const handleDetectar = async () => {
    if (!window.confirm('¿Iniciar detección automática de conflictos? El sistema buscará personas capturadas por más de una estructura.')) return;
    setDetectando(true);
    try {
      const res = await api.post('/conflictos/detectar');
      setMensaje({ tipo: 'exito', texto: res.data.mensaje });
      fetchConflictos();
      setTimeout(() => setMensaje(null), 5000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error en la detección.' });
    } finally {
      setDetectando(false);
    }
  };

  const handleEscalarTalento = async (conflictoId) => {
    if (!window.confirm('¿Escalar esta persona al Banco de Talento Zacateco?')) return;
    try {
      const res = await api.post(`/conflictos/${conflictoId}/escalar-talento`);
      setMensaje({ tipo: 'exito', texto: res.data.mensaje });
      fetchConflictos();
      setTimeout(() => setMensaje(null), 3000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.response?.data?.error || 'Error.' });
    }
  };

  const abrirResolver = (c) => {
    setConflictoResolviendo(c);
    setFormResolver({ estatus: c.estatus, resolucion_final: c.resolucion_final || '', prioridad: c.prioridad });
    setMostrarModalResolver(true);
  };

  const handleResolver = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/conflictos/${conflictoResolviendo.id}`, formResolver);
      setMensaje({ tipo: 'exito', texto: '✅ Conflicto actualizado.' });
      setMostrarModalResolver(false);
      fetchConflictos();
      setTimeout(() => setMensaje(null), 3000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al actualizar.' });
    }
  };

  if (!puedeAcceder) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
          style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
          <img src={logo} alt="DOMINIUM" className="h-16" />
          <button onClick={() => navigate('/dashboard')}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">← Volver</button>
        </header>
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <Lock className="text-gray-300 mb-4" size={56} />
          <p className="text-xl font-semibold text-gray-500">Acceso restringido</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <div className="flex items-center gap-3">
          <img src={logo} alt="DOMINIUM" className="h-16" />
          <div>
            <h1 className="font-bold text-lg">Conflictos Políticos</h1>
            <p className="text-green-200 text-xs">Disputas y casos en revisión</p>
          </div>
        </div>
        <button onClick={() => navigate('/dashboard')}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          ← Dashboard
        </button>
      </header>

      <main className="p-4 md:p-6 max-w-7xl mx-auto">

        {mensaje && (
          <div className={`mb-4 p-4 rounded-xl text-sm font-medium ${
            mensaje.tipo === 'exito' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
          }`}>{mensaje.texto}</div>
        )}

        {/* Estadísticas */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Total',        valor: stats.total || 0,         color: 'border-gray-400',  icono: '📋' },
            { label: 'Pendientes',   valor: stats.pendientes || 0,    color: 'border-yellow-500', icono: '⏳' },
            { label: 'En revisión',  valor: stats.en_revision || 0,   color: 'border-blue-500',  icono: '🔍' },
            { label: 'Resueltos',    valor: stats.resueltos || 0,     color: 'border-green-500', icono: '✅' },
            { label: 'Alta prioridad', valor: stats.alta_prioridad || 0, color: 'border-red-500', icono: '🔴' },
          ].map((s, i) => (
            <div key={i} className={`bg-white rounded-xl shadow-sm p-3 text-center border-l-4 ${s.color}`}>
              <p className="text-xl mb-1">{s.icono}</p>
              <p className="text-2xl font-bold text-gray-800">{s.valor}</p>
              <p className="text-xs text-gray-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filtros y acciones */}
        <div className="flex flex-wrap gap-3 mb-4 items-center">
          <select value={filtroEstatus} onChange={(e) => setFiltroEstatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white">
            <option value="">Todos los estatus</option>
            {ESTATUS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
          <select value={filtroPrioridad} onChange={(e) => setFiltroPrioridad(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white">
            <option value="">Todas las prioridades</option>
            {PRIORIDADES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white">
            <option value="">Todos los tipos</option>
            {TIPOS_CONFLICTO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>

          <div className="ml-auto flex gap-2">
            {puedeDetectar && (
              <button onClick={handleDetectar} disabled={detectando}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border-2 border-orange-400 text-orange-600 hover:bg-orange-50 transition disabled:opacity-50">
                <Zap size={16} />
                {detectando ? 'Detectando...' : '⚡ Detección automática'}
              </button>
            )}
            {puedeCrear && (
              <button onClick={() => setMostrarModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white hover:opacity-90 transition"
                style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                <Plus size={16} /> Nuevo Conflicto
              </button>
            )}
          </div>
        </div>

        {/* Tabla de conflictos */}
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Persona</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Tipo</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Estructuras</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Prioridad</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Estatus</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Responsable</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Fecha</th>
                <th className="text-left py-3 px-4 font-medium text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="py-8 text-center text-gray-400">Cargando...</td></tr>
              ) : conflictos.length === 0 ? (
                <tr>
                  <td colSpan="8" className="py-12 text-center">
                    <AlertTriangle className="text-gray-300 mx-auto mb-3" size={40} />
                    <p className="text-gray-400">No hay conflictos registrados</p>
                    {puedeDetectar && (
                      <button onClick={handleDetectar}
                        className="mt-3 text-orange-500 text-sm font-medium hover:underline">
                        ⚡ Ejecutar detección automática
                      </button>
                    )}
                  </td>
                </tr>
              ) : conflictos.map((c, i) => {
                const prioridad = PRIORIDADES.find(p => p.value === c.prioridad) || PRIORIDADES[1];
                const estatus   = ESTATUS.find(e => e.value === c.estatus) || ESTATUS[0];
                const tipo      = TIPOS_CONFLICTO.find(t => t.value === c.tipo_conflicto);
                return (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <p className="font-medium text-gray-800">{c.nombre} {c.apellido_paterno}</p>
                      <p className="text-xs text-gray-500">{c.telefono_principal} · Secc. {c.numero_seccion || '—'}</p>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs">
                        {tipo?.label || c.tipo_conflicto}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-600">
                      {c.nombre_estructura_a && (
                        <div>
                          <p className="font-medium">A: {c.nombre_estructura_a}</p>
                          {c.lider_a_nombre && <p className="text-gray-400">👤 {c.lider_a_nombre} {c.lider_a_apellido}</p>}
                        </div>
                      )}
                      {c.nombre_estructura_b && (
                        <div className="mt-1">
                          <p className="font-medium">B: {c.nombre_estructura_b}</p>
                          {c.lider_b_nombre && <p className="text-gray-400">👤 {c.lider_b_nombre} {c.lider_b_apellido}</p>}
                        </div>
                      )}
                      {!c.nombre_estructura_a && !c.nombre_estructura_b && <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${prioridad.color}`}>
                        {prioridad.label}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${estatus.color}`}>
                        {estatus.label}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-600">
                      {c.responsable_nombre ? `${c.responsable_nombre} ${c.responsable_apellido || ''}` : '—'}
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-500">
                      {new Date(c.created_at).toLocaleDateString('es-MX')}
                    </td>
                    <td className="py-3 px-4">
                      {/* Acción sugerida */}
                      {c.tipo_conflicto === 'disputa_estructura' && parseInt(c.num_estructuras) > 1 && (
                        <div className="mb-2">
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                            💡 Revisar o escalar
                          </span>
                        </div>
                      )}
                      {c.es_talento_potencial && (
                        <div className="mb-2">
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            ⭐ Perfil con potencial
                          </span>
                        </div>
                      )}
                      <div className="flex gap-1 flex-wrap">
                        <button onClick={() => abrirResolver(c)}
                          className="p-1.5 hover:bg-blue-100 rounded-lg transition flex items-center gap-1 text-xs text-blue-600 font-medium">
                          <Eye size={14} /> Revisar
                        </button>
                        {['superadmin','coordinador_general'].includes(user?.rol) && c.estatus !== 'resuelto' && (
                          <button onClick={() => handleEscalarTalento(c.id)}
                            title="Escalar al Banco de Talento"
                            className="p-1.5 hover:bg-yellow-100 rounded-lg transition text-xs text-yellow-600 font-medium flex items-center gap-1">
                            ⭐ Talento
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-center text-gray-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </main>

      {/* ── MODAL NUEVO CONFLICTO ── */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 sticky top-0"
              style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)', borderRadius: '1rem 1rem 0 0' }}>
              <h3 className="text-white font-bold">⚠️ Registrar Conflicto</h3>
              <button onClick={() => { setMostrarModal(false); setPersonaSeleccionada(null); setBusquedaPersona(''); }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCrearConflicto} className="p-6 space-y-4">
              {mensaje?.tipo === 'error' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">{mensaje.texto}</div>
              )}

              {/* Buscar persona */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Persona involucrada *</label>
                {personaSeleccionada ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div>
                      <p className="font-medium text-green-800">{personaSeleccionada.nombre} {personaSeleccionada.apellido_paterno}</p>
                      <p className="text-xs text-green-600">{personaSeleccionada.telefono_principal} · Secc. {personaSeleccionada.numero_seccion || '—'}</p>
                    </div>
                    <button type="button" onClick={() => setPersonaSeleccionada(null)}
                      className="text-green-600 hover:text-red-500 transition">
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
                      <input value={busquedaPersona} onChange={(e) => handleBuscarPersona(e.target.value)}
                        placeholder="Buscar por nombre o teléfono (mín. 3 caracteres)..."
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    {personasBuscadas.length > 0 && (
                      <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                        {personasBuscadas.map(p => (
                          <button type="button" key={p.id} onClick={() => { setPersonaSeleccionada(p); setPersonasBuscadas([]); }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0 text-sm">
                            <p className="font-medium">{p.nombre} {p.apellido_paterno}</p>
                            <p className="text-xs text-gray-500">{p.telefono_principal} · Secc. {p.numero_seccion || '—'} · {p.num_capturas} captura(s)</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de conflicto *</label>
                <select value={formConflicto.tipo_conflicto}
                  onChange={(e) => setFormConflicto({...formConflicto, tipo_conflicto: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required>
                  <option value="">Seleccionar...</option>
                  {TIPOS_CONFLICTO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
                <div className="flex gap-2">
                  {PRIORIDADES.map(p => (
                    <button type="button" key={p.value}
                      onClick={() => setFormConflicto({...formConflicto, prioridad: p.value})}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium border-2 transition ${
                        formConflicto.prioridad === p.value ? 'border-gray-800 ' + p.color : 'border-gray-200 text-gray-500'
                      }`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                <textarea value={formConflicto.descripcion}
                  onChange={(e) => setFormConflicto({...formConflicto, descripcion: e.target.value})}
                  rows={3} placeholder="Describe el conflicto con detalle..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button"
                  onClick={() => { setMostrarModal(false); setPersonaSeleccionada(null); setBusquedaPersona(''); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">
                  Cancelar
                </button>
                <button type="submit" disabled={guardando || !personaSeleccionada}
                  className="flex-1 text-white font-semibold py-2 rounded-lg hover:opacity-90 transition disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                  {guardando ? 'Guardando...' : 'Registrar Conflicto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL RESOLVER / REVISAR ── */}
      {mostrarModalResolver && conflictoResolviendo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 sticky top-0"
              style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)', borderRadius: '1rem 1rem 0 0' }}>
              <h3 className="text-white font-bold">🔍 Revisar Conflicto</h3>
              <button onClick={() => setMostrarModalResolver(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleResolver} className="p-6 space-y-4">
              {/* Info del conflicto */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <p className="font-semibold text-gray-800">
                  {conflictoResolviendo.nombre} {conflictoResolviendo.apellido_paterno}
                </p>
                <p className="text-sm text-gray-600">
                  📋 {TIPOS_CONFLICTO.find(t => t.value === conflictoResolviendo.tipo_conflicto)?.label}
                </p>
                {conflictoResolviendo.descripcion && (
                  <p className="text-sm text-gray-500 italic">"{conflictoResolviendo.descripcion}"</p>
                )}
                {conflictoResolviendo.nombre_estructura_a && (
                  <p className="text-xs text-gray-500">Estructura A: {conflictoResolviendo.nombre_estructura_a}</p>
                )}
                {conflictoResolviendo.nombre_estructura_b && (
                  <p className="text-xs text-gray-500">Estructura B: {conflictoResolviendo.nombre_estructura_b}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estatus</label>
                <div className="flex gap-2">
                  {ESTATUS.map(e => (
                    <button type="button" key={e.value}
                      onClick={() => setFormResolver({...formResolver, estatus: e.value})}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium border-2 transition ${
                        formResolver.estatus === e.value ? 'border-gray-800 ' + e.color : 'border-gray-200 text-gray-500'
                      }`}>
                      {e.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
                <div className="flex gap-2">
                  {PRIORIDADES.map(p => (
                    <button type="button" key={p.value}
                      onClick={() => setFormResolver({...formResolver, prioridad: p.value})}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium border-2 transition ${
                        formResolver.prioridad === p.value ? 'border-gray-800 ' + p.color : 'border-gray-200 text-gray-500'
                      }`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resolución / Notas</label>
                <textarea value={formResolver.resolucion_final}
                  onChange={(e) => setFormResolver({...formResolver, resolucion_final: e.target.value})}
                  rows={3} placeholder="Describe cómo se resolvió o las acciones tomadas..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setMostrarModalResolver(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">
                  Cancelar
                </button>
                <button type="submit"
                  className="flex-1 text-white font-semibold py-2 rounded-lg hover:opacity-90 transition"
                  style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
